import { useEffect, useState, memo, useCallback, useRef, useContext, createContext, useId, useMemo, ReactNode } from 'react';

import { userAPI } from '../UserAPI';
import Section from '../components/Section';
import { useAtom } from "jotai";
import { authenticationAtom } from '../State';
import { createdUserMessageKey } from '../utilities/entraSso';
import { useAdministrationAPI } from '../AdministrationAPI';

import { useTranslation } from 'react-i18next';
import { from, is, LegalPerson, RelationshipDirection, Role, RoleAssignment, RoleType, User } from '@the-real-insight/tri-model';
import { nameToRoleId } from '../roleUtils';
import RoleList from '../components/RoleList';
import { useDrop } from 'react-dnd';
import { useModalContext } from '../components/ModalProvider';
import { useUrlPageState } from '../utilities/dataTablePagination';
import { useGuideAction } from '../guide/useGuideAction';
import UserAppIndicator from './UserAppIndicator';
import NotificationEmailSection from './NotificationEmailSection';
import { usePushUsers } from './usePushUsers';
import { useRoleAccess } from '../hooks/useRoleAccess';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import type { Column, ColumnDef, OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    Info,
    Loader2,
    MousePointerClick,
    Plus,
    Search,
    Settings,
    Shield,
    Trash2,
    User as UserIcon,
    UserCheck,
    UserCog,
    UserPlus,
    Wallet,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

// ─── Table helpers ───────────────────────────────────────────────────────────

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp aria-hidden className="h-3.5 w-3.5" />
                : sorted === 'desc'
                    ? <ArrowDown aria-hidden className="h-3.5 w-3.5" />
                    : <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />}
        </Button>
    );
}

/** Leerzustand der Tabelle: Überschrift, Erklärung und — wo sinnvoll — Handlungsaufforderung. */
function TableEmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface EditedUser {
    email: string;
    firstName: string;
    lastName: string;
}

interface NewRole {
    name: string;
    description: string;
}

// ─── Droppable row ───────────────────────────────────────────────────────────

/**
 * Handlers for the role drop target, passed down as a ref so `DroppableRow`
 * keeps a stable identity — a component identity that changed per render would
 * remount every row (and abort any in-flight drag).
 */
type RowDropHandlers = {
    canDropRoleOnUser: (user: any, item: any) => boolean;
    handleAssignRole: (user: any, role: any) => void;
};

const RowDropContext = createContext<React.MutableRefObject<RowDropHandlers> | null>(null);

/** Whole user row acts as the drop target for a role. */
function DroppableRow({ user, children }: { user: any; children: ReactNode }) {
    const handlers = useContext(RowDropContext)!;

    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ['role'],
        canDrop: (item: any) => handlers.current.canDropRoleOnUser(user, item),
        drop: (item: any) => handlers.current.handleAssignRole(user, item),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }), [handlers, user]);

    return (
        <TableRow
            ref={drop}
            className={cn(
                'transition-colors',
                isOver && canDrop && 'bg-accent outline outline-2 -outline-offset-2 outline-primary',
                !isOver && canDrop && 'bg-accent/40 outline-dashed outline-2 -outline-offset-2 outline-primary/50',
            )}
        >
            {children}
        </TableRow>
    );
}

// ─── Role helpers ────────────────────────────────────────────────────────────

function getRoleId(role: Role): string {
    if (typeof role.getRoleID === 'function') return role.getRoleID();
    return (role as any).id;
}

/**
 * Icon + Farbklasse der Rolle. Die Kundenfarbe kommt ausschließlich über die
 * `--customer-*`-Variablen (Theme.tsx) mit Token-Fallback und färbt nur das
 * kleine Symbol — Fließtext bleibt auf den Basistokens, damit die Lesbarkeit
 * unabhängig von der Kundenpalette bleibt.
 */
const CUSTOMER_PRIMARY = 'text-[color:var(--customer-primary,hsl(var(--primary)))]';
const CUSTOMER_SECONDARY = 'text-[color:var(--customer-secondary,hsl(var(--primary)))]';
const CUSTOMER_TERTIARY = 'text-[color:var(--customer-tertiary,hsl(var(--primary)))]';
const CUSTOMER_PRIMARY_DARK = 'text-[color:var(--customer-primary-dark,hsl(var(--primary)))]';

function getRoleIcon(role: Role): { Icon: typeof UserIcon; colorClassName: string } {
    const roleId = getRoleId(role);

    if (roleId === 'customerAdministrator') return { Icon: Shield, colorClassName: CUSTOMER_PRIMARY };
    if (roleId === 'billingAdministrator') return { Icon: Wallet, colorClassName: CUSTOMER_SECONDARY };
    if (roleId === 'technicalAdministrator') return { Icon: Settings, colorClassName: CUSTOMER_TERTIARY };

    switch (role.type) {
        case RoleType.role: return { Icon: UserCog, colorClassName: CUSTOMER_PRIMARY };
        case RoleType.parameterizableRole: return { Icon: UserIcon, colorClassName: CUSTOMER_SECONDARY };
        case RoleType.parameterizedRole: return { Icon: UserCheck, colorClassName: CUSTOMER_TERTIARY };
        case RoleType.partnerDelegation: return { Icon: UserPlus, colorClassName: CUSTOMER_PRIMARY_DARK };
        default: return { Icon: UserIcon, colorClassName: CUSTOMER_PRIMARY };
    }
}

function isCustomerAdminRole(roleId: string): boolean {
    return roleId === 'customerAdministrator' || roleId?.startsWith('customerAdministrator:');
}

/**
 * Customer-scoped parameterizableRoles müssen über ihre erste Instanz zugewiesen werden.
 * Alle anderen Rollen werden direkt über ihre eigene _id zugewiesen.
 */
function resolveRoleIdForAssignment(roleData: any): string {
    const role = new Role(roleData);
    if (role.type === RoleType.parameterizableRole && roleData.instances?.length > 0) {
        return roleData.instances[0]._id;
    }
    return roleData._id;
}

// ─── RoleBadge ───────────────────────────────────────────────────────────────

const RoleBadge = memo(function RoleBadge(props: {
    roleAssignment: RoleAssignment;
    canRemove: boolean;
    isRemoving: boolean;
    isAdding: boolean;
    customerName: string;
    removeLabel: string;
    onRemove: () => void;
}) {
    const { t } = useTranslation();

    const role = props.roleAssignment.role;
    if (!role) return null;

    const { Icon, colorClassName } = getRoleIcon(role);
    const roleId = getRoleId(role);

    // Versuche übersetzten Namen/Beschreibung, sonst Fallback auf Model-Daten
    const nameKey = `settingsPanel.userManagementPanel.roleName_${roleId}`;
    const translatedName = t(nameKey);
    const roleName = translatedName !== nameKey ? translatedName : (role.getRoleName?.() ?? (role as any)?.name ?? '');

    const descKey = `settingsPanel.userManagementPanel.roleDescription_${roleId}`;
    const translatedDesc = t(descKey, { customerName: props.customerName });
    const description = translatedDesc !== descKey ? translatedDesc : (role.description || '');

    return (
        <div
            title={description}
            className={cn(
                'flex min-w-0 items-center gap-2 overflow-hidden rounded-md bg-muted transition-all duration-300',
                props.isRemoving
                    ? 'max-h-0 -translate-x-2.5 scale-95 px-1.5 py-0 opacity-0'
                    : 'max-h-10 px-1.5 py-1 opacity-100',
                props.isAdding && 'animate-in fade-in slide-in-from-right-2 duration-300',
            )}
        >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background">
                <Icon aria-hidden className={cn('h-3 w-3', colorClassName)} />
            </span>
            <span className="min-w-0 flex-1 truncate font-bold">{roleName}</span>
            {props.canRemove && (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 rounded-full text-muted-foreground max-sm:h-9 max-sm:w-9"
                    aria-label={props.removeLabel}
                    title={props.removeLabel}
                    onClick={props.onRemove}
                >
                    <X aria-hidden className="h-3 w-3" />
                </Button>
            )}
        </div>
    );
});

// ─── Component ───────────────────────────────────────────────────────────────

const USERS_PAGE_SIZE = 10;

/** Spaltenbreiten der Team-Tabelle (`table-fixed`); die Rollenspalte nimmt den Rest. */
const COLUMN_WIDTHS: Record<string, string | undefined> = {
    user: 'w-[45%]',
    actions: 'w-[72px]',
};

export default function UserManagementPanel() {
    const { t } = useTranslation();
    const { page, onPageChange } = useUrlPageState('userListPage');
    const { pushError, pushSuccess, showDeleteDialog } = useModalContext() as any;
    const { createUser, deleteUser } = useAdministrationAPI();
    const [authentication] = useAtom(authenticationAtom) as any;
    const roleAccess = useRoleAccess();
    const fieldId = useId();

    // Who has the app set up with push: a failed or missing answer just shows no mark.
    const pushUsers = usePushUsers(authentication?.customer, true);
    const usersWithApp = useMemo(
        () => new Set(pushUsers.users.filter((candidate) => candidate.pushActive).map((candidate) => String(candidate.userId))),
        [pushUsers.users],
    );

    const [users, setUsers] = useState<any[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [removingRoles, setRemovingRoles] = useState<Set<string>>(new Set());
    const [addingRoles, setAddingRoles] = useState<Set<string>>(new Set());
    const [rolesChanged, setRolesChanged] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [sorting, setSorting] = useState<SortingState>([]);

    // ── Rolle anlegen ────────────────────────────────────────────────────
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [creatingRole, setCreatingRole] = useState(false);
    const [roleRefreshSignal, setRoleRefreshSignal] = useState(0);

    const searchId = `${fieldId}-search`;

    // ── Formulare ────────────────────────────────────────────────────────

    // Gleiche Bedingungen wie die frühere `isFormValid`: alle drei Felder
    // gefüllt, die E-Mail enthält „@“ und „.“ und ist länger als 5 Zeichen.
    const inviteSchema = useMemo(() => z.object({
        email: z
            .string()
            .trim()
            .min(1, { message: t('settingsPanel.userManagementPanel.emailNotValid') })
            .refine(
                (value) => value.includes('@') && value.includes('.') && value.length > 5,
                { message: t('settingsPanel.userManagementPanel.emailNotValid') },
            ),
        firstName: z.string().trim().min(1, { message: t('settingsPanel.userManagementPanel.firstNameNotValid') }),
        lastName: z.string().trim().min(1, { message: t('settingsPanel.userManagementPanel.lastNameNotValid') }),
    }), [t]);

    const roleSchema = useMemo(() => z.object({
        name: z
            .string()
            .trim()
            .min(1, { message: t('settingsPanel.userManagementPanel.newRoleNameNotValid') })
            .refine((value) => !!nameToRoleId(value), {
                message: t('settingsPanel.userManagementPanel.newRoleNameNotValid'),
            }),
        description: z.string(),
    }), [t]);

    const inviteForm = useForm<EditedUser>({
        resolver: zodResolver(inviteSchema),
        mode: 'onChange',
        defaultValues: { email: '', firstName: '', lastName: '' },
    });

    const roleForm = useForm<NewRole>({
        resolver: zodResolver(roleSchema),
        mode: 'onChange',
        defaultValues: { name: '', description: '' },
    });

    // ── Daten laden ──────────────────────────────────────────────────────

    const loadUsers = useCallback(async () => {
        try {
            const newUsers = await userAPI.query(
                from(User.type)
                    .where(is('customer').equal(authentication.customer))
                    .link('roleAssignments', RelationshipDirection.out, RoleAssignment.type)
                    .link('role', RelationshipDirection.out, Role.type),
            );

            // Unpopulated role links come back as id strings — resolve so RoleBadge has names.
            const roleIdStrings = new Set<string>();
            for (const user of newUsers) {
                if (!user?._id || !Array.isArray(user.roleAssignments)) continue;
                for (const ra of user.roleAssignments) {
                    if (typeof ra.role === 'string' && ra.role) roleIdStrings.add(ra.role);
                }
            }

            const roleByIdString = new Map<string, Role>();
            await Promise.all(
                Array.from(roleIdStrings).map(async (id) => {
                    try {
                        const role = await userAPI.getEntityByID(Role.type.name, id);
                        roleByIdString.set(id, role);
                    } catch {
                        /* entity may be inaccessible */
                    }
                }),
            );

            // Rolle-Instanzen erstellen und Issuer (Herausgeber) auflösen
            const issuerCache: Record<string, LegalPerson> = {};

            for (const user of newUsers) {
                if (!user?._id || !Array.isArray(user.roleAssignments)) continue;

                for (const ra of user.roleAssignments) {
                    if (typeof ra.role === 'string') {
                        const resolved = roleByIdString.get(ra.role);
                        if (!resolved) continue;
                        // Avoid sharing one mutable Role instance across multiple users.
                        ra.role = new Role(resolved as any);
                    } else if (ra.role && typeof ra.role === 'object') {
                        const raw = ra.role as any;
                        const mongoLikeId =
                            raw._id
                            ?? (typeof raw.id === 'string' && /^[a-f\d]{24}$/i.test(raw.id) ? raw.id : undefined);
                        if (!mongoLikeId) continue;
                        ra.role = new Role({ ...raw, _id: mongoLikeId });
                    } else {
                        continue;
                    }

                    // Issuer-ID durch das volle LegalPerson-Objekt ersetzen
                    if (ra.role.issuer) {
                        const issuer = ra.role.issuer as any;

                        if (typeof issuer === 'string' && issuer) {
                            if (!issuerCache[issuer]) {
                                const person: any = await userAPI.queryOne(
                                    from(LegalPerson.type).where(is('_id').equal(issuer)),
                                );
                                if (person?._id) issuerCache[person._id] = person;
                            }

                            if (issuerCache[issuer]) {
                                ra.role.issuer = issuerCache[issuer];
                            }
                        } else if (issuer && typeof issuer === 'object' && typeof issuer._id === 'string') {
                            issuerCache[issuer._id] = issuer as LegalPerson;
                        }
                    }
                }
            }

            setUsers(newUsers);
        } catch (error) {
            pushError(error);
        } finally {
            setUsersLoading(false);
        }
    }, [authentication?.customer, pushError]);

    useEffect(() => {
        void loadUsers();

        userAPI.getCustomer(authentication.customer)
            .then((customer) => { if (customer?.name) setCustomerName(customer.name); })
            .catch(() => { /* Customer-Name ist optional */ });
    }, [authentication?.customer, loadUsers]);

    // ── Rolle zuweisen (Drag & Drop) ─────────────────────────────────────

    function canDropRoleOnUser(user: any, droppedItem: any): boolean {
        if (!roleAccess.canManageRoles) return false;

        const droppedRole = new Role(droppedItem);
        const droppedId = getRoleId(droppedRole);
        const alreadyAssigned = user.roleAssignments?.some(
            (ra: RoleAssignment) => ra.role && getRoleId(ra.role) === droppedId,
        );

        return !alreadyAssigned;
    }

    async function handleAssignRole(user: any, droppedRoleData: any) {
        const roleIdToAssign = resolveRoleIdForAssignment(droppedRoleData);
        const droppedRole = new Role(droppedRoleData);
        const tempId = `temp-${Date.now()}`;

        // Optimistisch: Rolle sofort in der UI anzeigen
        setAddingRoles(prev => new Set(prev).add(tempId));
        setUsers(prev => prev.map(u => {
            if (u._id !== user._id) return u;
            return { ...u, roleAssignments: [...(u.roleAssignments || []), { _id: tempId, role: droppedRole }] };
        }));

        // Animations-Flag nach 300ms entfernen
        setTimeout(() => {
            setAddingRoles(prev => {
                const next = new Set(prev);
                next.delete(tempId);
                return next;
            });
        }, 300);

        try {
            await userAPI.createRoleAssignment(authentication.customer, user._id, roleIdToAssign);
            setRolesChanged(true);
            await loadUsers();
        } catch (error) {
            pushError(error);

            // Rollback: temporäre Rolle wieder entfernen
            setUsers(prev => prev.map(u => {
                if (u._id !== user._id) return u;
                return { ...u, roleAssignments: (u.roleAssignments || []).filter((ra: any) => ra._id !== tempId) };
            }));
        }
    }

    // ── Rolle entfernen ──────────────────────────────────────────────────

    function countCustomerAdmins(): number {
        let count = 0;
        for (const user of users) {
            for (const ra of user.roleAssignments || []) {
                if (ra.role && isCustomerAdminRole(getRoleId(ra.role))) {
                    count++;
                }
            }
        }
        return count;
    }

    function canRemoveRole(roleAssignment: RoleAssignment): boolean {
        if (!roleAccess.canManageRoles) return false;

        // Letzten Customer-Admin darf man nicht entfernen
        const roleId = roleAssignment.role ? getRoleId(roleAssignment.role) : '';
        if (isCustomerAdminRole(roleId) && countCustomerAdmins() <= 1) return false;

        return true;
    }

    async function handleRemoveRole(user: any, roleAssignment: RoleAssignment) {
        const raId = roleAssignment._id || '';

        // Erst Animation starten, dann nach 300ms entfernen
        setRemovingRoles(prev => new Set(prev).add(raId));

        setTimeout(async () => {
            // Optimistisch aus der UI entfernen
            setUsers(prev => prev.map(u => {
                if (u._id !== user._id) return u;
                return { ...u, roleAssignments: (u.roleAssignments || []).filter((ra: any) => (ra._id || '') !== raId) };
            }));
            setRemovingRoles(prev => {
                const next = new Set(prev);
                next.delete(raId);
                return next;
            });

            try {
                await userAPI.deleteRoleAssignment(
                    authentication.customer,
                    { _id: String(roleAssignment._id), role: String(roleAssignment.role?._id) } as any,
                );
                setRolesChanged(true);
            } catch (error) {
                pushError(error);
                await loadUsers(); // Bei Fehler: echten Stand vom Server holen
            }
        }, 300);
    }

    // ── Rolle anlegen ────────────────────────────────────────────────────

    const openRoleDialog = () => {
        roleForm.reset({ name: '', description: '' });
        setRoleDialogOpen(true);
    };

    async function handleCreateRole(values: NewRole) {
        const name = values.name.trim();
        const roleId = nameToRoleId(name);
        if (!name || !roleId || !authentication?.customer) return;
        setCreatingRole(true);
        try {
            const role = new Role({
                name,
                id: roleId,
                description: values.description.trim() || null,
                type: RoleType.role,
                _tenant: authentication.customer,
            } as any);
            await userAPI.createRole(authentication.customer, role);
            pushSuccess(t('settingsPanel.userManagementPanel.roleCreated'));
            setRoleDialogOpen(false);
            roleForm.reset({ name: '', description: '' });
            setRoleRefreshSignal((signal) => signal + 1);
        } catch (error) {
            pushError(error);
        } finally {
            setCreatingRole(false);
        }
    }

    // ── User löschen ─────────────────────────────────────────────────────

    function handleDeleteUser(user: any) {
        showDeleteDialog(
            t('global.deleteUser'),
            `${user.firstName} ${user.lastName}`,
            async () => {
                try {
                    await deleteUser(authentication.customer, user);
                    pushSuccess(t('settingsPanel.userManagementPanel.userInvalidated'));
                    await loadUsers();
                } catch (error) {
                    pushError(error);
                }
            },
        );
    }

    // ── User einladen ────────────────────────────────────────────────────

    const openUserDialog = () => {
        inviteForm.reset({ email: '', firstName: '', lastName: '' });
        setUserDialogOpen(true);
    };

    /**
     * Prefill from the address
     * (`?panel=teamManagement&guide=settings.teamManagement.invite&email=…&firstName=…&lastName=…`):
     * the invite dialog opens prefilled; the invitation is only sent on click.
     *
     * All three fields are personal data (`guideAction.personalFields`) and sit in the browser
     * history until the hook cleans up — so it removes them in the same pass in which it opens the
     * dialog. The dialog does not assign a role; that happens later in the table.
     */
    useGuideAction(
        'settings.teamManagement.invite',
        (values) => {
            inviteForm.reset({
                email: values.email ?? '',
                firstName: values.firstName ?? '',
                lastName: values.lastName ?? '',
            });
            setUserDialogOpen(true);
        },
        { panel: 'teamManagement' },
    );

    async function handleInvite(values: EditedUser) {
        setInviting(true);
        try {
            const trimmedUser = {
                email: values.email.trim(),
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
            };
            const created = await createUser(authentication.customer, trimmedUser);
            pushSuccess(t(createdUserMessageKey(created)));
            inviteForm.reset({ email: '', firstName: '', lastName: '' });
            setUserDialogOpen(false);
            await loadUsers();
        } catch (error) {
            pushError(error);
        } finally {
            setInviting(false);
        }
    }

    // ── Suche ────────────────────────────────────────────────────────────

    function filterUsers(users: any[]): any[] {
        if (!searchQuery) return users;

        const query = searchQuery.toLowerCase();
        return users.filter(user =>
            user.firstName?.toLowerCase().includes(query)
            || user.lastName?.toLowerCase().includes(query)
            || user.email?.toLowerCase().includes(query),
        );
    }

    // ── Drop-Handler für die Rows ────────────────────────────────────────

    const rowDropHandlers = useRef<RowDropHandlers>({ canDropRoleOnUser, handleAssignRole });
    rowDropHandlers.current = { canDropRoleOnUser, handleAssignRole };

    // ── Tabellen-Spalten ─────────────────────────────────────────────────

    const columns: ColumnDef<any>[] = [
        {
            id: 'user',
            accessorFn: (user: User) => (user.lastName || '').toLowerCase(),
            header: ({ column }) => (
                <SortableHeader column={column}>
                    {t('settingsPanel.userManagementPanel.user')}
                </SortableHeader>
            ),
            cell: ({ row }) => {
                const user = row.original;
                const initials = `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`;

                return (
                    <div className="flex min-w-0 items-center gap-3">
                        <Avatar className="h-10 w-10 shrink-0">
                            <AvatarFallback>{initials || <UserIcon aria-hidden className="h-4 w-4" />}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                            <div className="truncate text-sm font-semibold">{user.firstName} {user.lastName}</div>
                            <div className="truncate text-sm text-muted-foreground">{user.email}</div>
                            <UserAppIndicator active={usersWithApp.has(String(user._id))} />
                        </div>
                    </div>
                );
            },
        },
        {
            id: 'roles',
            enableSorting: false,
            header: () => (
                <span className="font-bold text-foreground">
                    {t('settingsPanel.userManagementPanel.roles')}
                </span>
            ),
            cell: ({ row }) => {
                const user = row.original;

                return (
                    <div className="flex min-h-[48px] w-full min-w-0 items-center">
                        {user.roleAssignments?.length > 0 && (
                            <div className="flex w-full min-w-0 flex-col gap-1 py-1">
                                {user.roleAssignments.map((ra: RoleAssignment) => (
                                    <RoleBadge
                                        key={ra._id || ''}
                                        roleAssignment={ra}
                                        canRemove={canRemoveRole(ra)}
                                        isRemoving={removingRoles.has(ra._id || '')}
                                        isAdding={addingRoles.has(ra._id || '')}
                                        customerName={customerName}
                                        removeLabel={t('settingsPanel.userManagementPanel.removeRole')}
                                        onRemove={() => handleRemoveRole(user, ra)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'actions',
            enableSorting: false,
            header: () => (
                <span className="sr-only">{t('settingsPanel.userManagementPanel.actions')}</span>
            ),
            cell: ({ row }) => {
                const user = row.original;

                return user.id !== authentication.id
                    ? (
                        <div className="flex justify-end">
                            <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t('global.deleteUser')}
                                title={t('global.deleteUser')}
                                onClick={() => handleDeleteUser(user)}
                            >
                                <Trash2 aria-hidden className="h-4 w-4" />
                            </Button>
                        </div>
                    )
                    : null;
            },
        },
    ];

    const filteredUsers = filterUsers(users);

    /*
     * Klammerung auf die letzte Seite — das Gegenstück zu `autoResetPageIndex: false` unten.
     *
     * Ohne die Rücksetzung braucht es eine Grenze nach oben: Schrumpft die Liste unter die gewählte
     * Seite (Suche, gelöschter Nutzer) oder steht in der Adresse eine Seite, die es nicht mehr gibt,
     * zeigt die Tabelle die letzte, die es gibt. Nicht während des Ladens: Ein leerer Zwischenstand
     * darf die gewählte Seite nicht verwerfen.
     */
    const requestedPageIndex = Math.max(0, page - 1);
    const pageCount = Math.ceil(filteredUsers.length / USERS_PAGE_SIZE);
    const clampedPageIndex =
        usersLoading || pageCount === 0
            ? requestedPageIndex
            : Math.min(requestedPageIndex, pageCount - 1);

    const onPageChangeRef = useRef(onPageChange);
    onPageChangeRef.current = onPageChange;

    useEffect(() => {
        if (clampedPageIndex !== requestedPageIndex) {
            onPageChangeRef.current(clampedPageIndex + 1);
        }
    }, [clampedPageIndex, requestedPageIndex]);

    const paginationState: PaginationState = { pageIndex: clampedPageIndex, pageSize: USERS_PAGE_SIZE };

    const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
        const next = typeof updater === 'function' ? updater(paginationState) : updater;
        // Nur melden, was die Seite wirklich ändert — sonst schreibt jede Rücksetzung die Adresse
        // neu, die Tabelle rendert, und der Kreis beginnt von vorn.
        if (next.pageIndex !== paginationState.pageIndex) {
            onPageChange(next.pageIndex + 1);
        }
    };

    const table = useReactTable({
        data: filteredUsers,
        columns,
        state: {
            sorting,
            pagination: paginationState,
        },
        onSortingChange: setSorting,
        onPaginationChange: handlePaginationChange,
        /*
         * TanStack setzt die Seite bei jeder Datenänderung auf die erste zurück (Standard). Hier
         * ändern sich die Daten bei jeder Rollenvergabe: Der optimistische Chip und das anschließende
         * Nachladen schreiben `users` neu — und wer eine Rolle auf Seite 2 abgelegt hatte, stand
         * danach wieder auf Seite 1. Die Seite bleibt stehen; was über das Ende hinausragt, klammert
         * der Effekt oben.
         */
        autoResetPageIndex: false,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const showPagination = !usersLoading && filteredUsers.length > USERS_PAGE_SIZE;

    // ── Render ───────────────────────────────────────────────────────────

    return (
        <>
            <div className="flex w-full min-w-0 max-w-[1400px] flex-wrap items-start gap-4">

                {/* Linke Seite: Team-Tabelle */}
                <Section className="min-w-[320px] flex-1" title={t('settingsPanel.userManagementPanel.teamMembers')}>

                    {/* Hinweis: Rollen-Änderungen werden erst beim nächsten Login wirksam */}
                    {rolesChanged && (
                        <Alert variant="info" className="mb-2">
                            <Info aria-hidden className="h-4 w-4" />
                            <AlertDescription>
                                {t('settingsPanel.userManagementPanel.rolesChangedLoginHint')}
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Suchleiste + Einladen-Button */}
                    <div className="mb-2 flex min-w-0 flex-wrap items-end justify-between gap-2">
                        <div className="flex min-w-[200px] flex-1 flex-col gap-1.5 sm:max-w-[400px]">
                            <Label htmlFor={searchId}>{t('settingsPanel.userManagementPanel.searchUsersLabel')}</Label>
                            <div className="relative min-w-0">
                                <Search
                                    aria-hidden
                                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                                />
                                <Input
                                    id={searchId}
                                    className="px-9"
                                    type="search"
                                    value={searchQuery}
                                    placeholder={t('settingsPanel.userManagementPanel.searchUsers')}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                />
                                {searchQuery && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-0 top-0 h-9 w-9 max-sm:h-9"
                                        aria-label={t('settingsPanel.userManagementPanel.clearSearch')}
                                        title={t('settingsPanel.userManagementPanel.clearSearch')}
                                        onClick={() => setSearchQuery('')}
                                    >
                                        <X aria-hidden className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                        <Button data-guide-id="settings.teamManagement.invite" variant="outline" className="gap-2 max-sm:w-full" onClick={openUserDialog}>
                            <UserPlus aria-hidden className="h-4 w-4" />
                            {t('settingsPanel.userManagementPanel.inviteUser')}
                        </Button>
                    </div>

                    {/* User-Tabelle */}
                    <div className="min-w-0 overflow-x-auto">
                        <div className="rounded-md border">
                            {/* table-fixed: sonst zwingt der nicht umbrechende Rollenname die
                                Tabelle über die Kartenbreite hinaus. min-w sorgt dafür, dass sie
                                auf schmalen Viewports scrollt statt zu quetschen. */}
                            <Table className="min-w-[560px] table-fixed">
                                <TableHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => (
                                                <TableHead key={header.id} className={COLUMN_WIDTHS[header.column.id]}>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {usersLoading ? (
                                        Array.from({ length: 5 }).map((_, rowIndex) => (
                                            <TableRow key={`skeleton-${rowIndex}`}>
                                                {columns.map((_column, cellIndex) => (
                                                    <TableCell key={cellIndex}>
                                                        <Skeleton className="h-4 w-full" />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : table.getRowModel().rows.length > 0 ? (
                                        <RowDropContext.Provider value={rowDropHandlers}>
                                            {table.getRowModel().rows.map((row) => (
                                                <DroppableRow key={row.id} user={row.original}>
                                                    {row.getVisibleCells().map((cell) => (
                                                        <TableCell key={cell.id}>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </TableCell>
                                                    ))}
                                                </DroppableRow>
                                            ))}
                                        </RowDropContext.Provider>
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                                                {searchQuery
                                                    ? <TableEmptyState
                                                        title={t('settingsPanel.userManagementPanel.noMatchingUsersTitle')}
                                                        hint={t('settingsPanel.userManagementPanel.noMatchingUsersHint')}
                                                        action={
                                                            <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>
                                                                {t('settingsPanel.userManagementPanel.clearSearch')}
                                                            </Button>
                                                        }
                                                    />
                                                    : <TableEmptyState
                                                        title={t('settingsPanel.userManagementPanel.noUsersTitle')}
                                                        hint={t('settingsPanel.userManagementPanel.noUsersHint')}
                                                        action={
                                                            <Button data-guide-id="settings.teamManagement.invite" variant="outline" size="sm" className="gap-2" onClick={openUserDialog}>
                                                                <UserPlus aria-hidden className="h-4 w-4" />
                                                                {t('settingsPanel.userManagementPanel.inviteUser')}
                                                            </Button>
                                                        }
                                                    />}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            {showPagination && (
                                <div className="flex items-center justify-end gap-2 border-t px-4 py-2">
                                    <div className="mr-auto text-sm text-muted-foreground">
                                        {table.getState().pagination.pageIndex + 1} / {Math.max(table.getPageCount(), 1)}
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        aria-label={t('global.back')}
                                        onClick={() => table.previousPage()}
                                        disabled={!table.getCanPreviousPage()}
                                    >
                                        <ChevronLeft aria-hidden className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        aria-label={t('global.next')}
                                        onClick={() => table.nextPage()}
                                        disabled={!table.getCanNextPage()}
                                    >
                                        <ChevronRight aria-hidden className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </Section>

                {/* Rechte Seite: Rollen-Liste zum Drag & Drop */}
                <Section className="w-full min-w-[280px] lg:w-[420px]" title={t('settingsPanel.userManagementPanel.roles')}>
                    <div className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <MousePointerClick aria-hidden className="h-4 w-4 shrink-0" />
                        <span className="min-w-0">{t('settingsPanel.userManagementPanel.dragRoleHint')}</span>
                    </div>
                    {roleAccess.canManageRoles && (
                        <div className="mb-2 flex flex-wrap">
                            <Button data-guide-id="settings.teamManagement.createRole" variant="outline" className="gap-2 max-sm:w-full" onClick={openRoleDialog}>
                                <Plus aria-hidden className="h-4 w-4" />
                                {t('settingsPanel.userManagementPanel.createRole')}
                            </Button>
                        </div>
                    )}
                    <RoleList
                        className="min-w-0"
                        showParameterizedRoles
                        showDelegatedRoles
                        showIssuer
                        customerName={customerName}
                        onRoleDeleted={loadUsers}
                        refreshSignal={roleRefreshSignal}
                    />
                </Section>

                {roleAccess.canManageRoles && <NotificationEmailSection className="w-full" />}
            </div>

            {/* Dialog: Neuen User einladen */}
            <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogContent
                    className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>{t('settingsPanel.userManagementPanel.inviteUser')}</DialogTitle>
                    </DialogHeader>
                    <Form {...inviteForm}>
                        <form onSubmit={inviteForm.handleSubmit(handleInvite)} className="space-y-4">
                            <FormField
                                control={inviteForm.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('settingsPanel.userManagementPanel.email')}</FormLabel>
                                        <FormControl>
                                            <Input type="email" autoComplete="email" placeholder="name@example.com" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={inviteForm.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('settingsPanel.userManagementPanel.firstName')}</FormLabel>
                                        <FormControl>
                                            <Input autoComplete="given-name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={inviteForm.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('settingsPanel.userManagementPanel.lastName')}</FormLabel>
                                        <FormControl>
                                            <Input autoComplete="family-name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <DialogFooter className="gap-2 pt-4 sm:gap-0">
                                <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                                    {t('global.cancel')}
                                </Button>
                                <Button type="submit" disabled={!inviteForm.formState.isValid || inviting}>
                                    {inviting && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                                    {inviting
                                        ? t('settingsPanel.userManagementPanel.inviting')
                                        : t('settingsPanel.userManagementPanel.invite')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            {/* Dialog: Neue Rolle anlegen */}
            <Dialog
                open={roleDialogOpen}
                onOpenChange={(open) => { if (!creatingRole) setRoleDialogOpen(open); }}
            >
                <DialogContent
                    className="max-w-[calc(100vw-2rem)] sm:max-w-[520px]"
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>{t('settingsPanel.userManagementPanel.createRole')}</DialogTitle>
                        <DialogDescription>
                            {t('settingsPanel.userManagementPanel.createRoleHint')}
                        </DialogDescription>
                    </DialogHeader>
                    <Form {...roleForm}>
                        <form onSubmit={roleForm.handleSubmit(handleCreateRole)} className="space-y-4">
                            <FormField
                                control={roleForm.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('settingsPanel.userManagementPanel.newRoleName')}
                                            <span aria-hidden className="ml-0.5 text-destructive">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={roleForm.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('settingsPanel.userManagementPanel.newRoleDescription')}</FormLabel>
                                        <FormControl>
                                            <Textarea rows={2} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <DialogFooter className="gap-2 pt-4 sm:gap-0">
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={creatingRole}
                                    onClick={() => setRoleDialogOpen(false)}
                                >
                                    {t('global.cancel')}
                                </Button>
                                <Button type="submit" disabled={creatingRole || !roleForm.formState.isValid}>
                                    {creatingRole && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                                    {creatingRole
                                        ? t('settingsPanel.userManagementPanel.creatingRole')
                                        : t('settingsPanel.userManagementPanel.create')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        </>
    );
}
